import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import api from "../api/axios";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';

// Validation schema
const schema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format'),

  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });



const navigate = useNavigate();
const onSubmit = async (data) => {
  setIsSubmitting(true);

  try {
    const response = await api.post("users/login/", {
      first_name: data.first_name,
      email: data.email,
      username: data.username,
      password: data.password,
    });

    localStorage.setItem("access", response.data.access);
    localStorage.setItem("refresh", response.data.refresh);

    localStorage.setItem("user", JSON.stringify(response.data.user));

    navigate("/");
    reset();
  } catch (error) {
    const message =
      error.response?.data?.error ||
      error.response?.data?.detail ||
      "Login failed";

    alert(message);
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8 col-sm-10">
          <div className="card shadow-lg border-0 rounded-lg mt-5">
            <div className="card-header bg-gradient-primary text-white">
              <h3 className="text-center font-weight-light my-2">Welcome Back</h3>
              <p className="text-center mb-0 text-primary">Sign in to continue to your account</p>
            </div>

            <div className="card-body p-4 p-md-5">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Email */}
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      placeholder="name@example.com"
                      {...register('email')}
                    />
                    <label htmlFor="email">
                      <i className="bi bi-envelope me-2"></i>
                      Email Address *
                    </label>
                    {errors.email && (
                      <div className="invalid-feedback d-block">
                        {errors.email.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Password */}
                <div className="mb-4">
                  <div className="form-floating position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      placeholder="Password"
                      {...register('password')}
                    />
                    <label htmlFor="password">
                      <i className="bi bi-lock me-2"></i>
                      Password *
                    </label>
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ zIndex: 3 }}
                    >
                      {showPassword ? (
                        <i className="bi bi-eye-slash"></i>
                      ) : (
                        <i className="bi bi-eye"></i>
                      )}
                    </button>
                    {errors.password && (
                      <div className="invalid-feedback d-block">
                        {errors.password.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Remember Me & Forgot Password Row */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>

                  <div>
                    <Link to="/forgot-password" className="text-primary text-decoration-none">
                      <i className="bi bi-key me-1"></i>
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="d-grid gap-2 mb-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-box-arrow-in-right me-2"></i>
                        Sign In
                      </>
                    )}
                  </button>
                </div>

                {/* Divider */}
                <div className="position-relative my-4">
                  <hr />
                  <div className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted">
                    or continue with
                  </div>
                </div>

                {/* Social Login Buttons */}
                <div className="row g-2 mb-4">
                  <div className="col-md-6">
                    <button
                      type="button"
                      className="btn btn-outline-dark w-100"
                      onClick={() => alert('Google login coming soon!')}
                    >
                      <i className="bi bi-google me-2"></i>
                      Google
                    </button>
                  </div>
                  <div className="col-md-6">
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={() => alert('Facebook login coming soon!')}
                    >
                      <i className="bi bi-facebook me-2"></i>
                      Facebook
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="card-footer text-center py-3">
              <div className="small">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary text-decoration-none fw-bold">
                  Create one here
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;